/**
 * related-collection-chips.js                                    [CRO edits]
 *
 * Renders the category chips at the top of collection AND search pages from
 * assets/CollectionChips.json.
 *
 * HOW A ROW IS CHOSEN
 *   Collection page - the collection's own row, else the row of its parent so
 *     siblings stay reachable (the map is keyed by parent, so without this
 *     fallback 94 of 114 chips led to a page with no chips at all).
 *   Search page - the best-scoring row for the query, comparing it against
 *     each row's title, its aliases, and its chip labels. "قماش صيفي" selects
 *     the قماش صيفي row; "بلوزات" selects ملابس because بلوزات is one of its
 *     chips. Below a confidence threshold nothing is shown, so a random query
 *     never gets an arbitrary row.
 *
 * A chip is hidden when its collection has no products (count === 0), no
 * handle, or has been deleted. Counts are a snapshot - re-run
 * ~/mahitab_fabric_work/regen_collection_chips.py after re-merchandising.
 *
 * Chips are real <a href="/collections/…"> links. They used to be href="#"
 * with a Storefront API round trip per click just to resolve the handle.
 */
(function () {
  'use strict';

  var THUMB = 96;        // px requested from the CDN; displayed at 34px (2x for retina)
  var MIN_SCORE = 30;    // below this a search query is treated as "no confident row"

  /* ---------------------------------------------------------------- utils */

  // Arabic is written several ways for the same word: أ/إ/آ vs ا, ة vs ه,
  // ى vs ي. Titles also carry emoji (اطفال👧, تريكو ⭐). Fold all of it away
  // so matching compares meaning, not orthography.
  function norm(s) {
    if (!s) return '';
    return String(s)
      .replace(/[ً-ْـ]/g, '')     // harakat + tatweel
      .replace(/[أإآ]/g, 'ا') // أ إ آ -> ا
      .replace(/ة/g, 'ه')               // ة -> ه
      .replace(/ى/g, 'ي')               // ى -> ي
      .replace(/ؤ/g, 'و')               // ؤ -> و
      .replace(/ئ/g, 'ي')               // ئ -> ي
      .toLowerCase()
      .replace(/[^ء-يa-z0-9\s]/g, ' ')  // drop emoji/punctuation
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokens(s) {
    var n = norm(s);
    return n ? n.split(' ') : [];
  }

  function phraseHit(haystack, needle) {
    if (!haystack || !needle) return false;
    return (' ' + haystack + ' ').indexOf(' ' + needle + ' ') !== -1;
  }

  /* --------------------------------------------------------------- scoring */

  // Product counts span 1 -> ~1,900, so weight popularity on a log scale:
  // it separates a 13-product corner from a 1,440-product category without
  // letting sheer size beat relevance outright.
  function popBonus(count) {
    if (!count || count <= 0) return 0;
    return Math.min(30, Math.round(30 * Math.log(1 + count) / Math.log(2001)));
  }

  // Arabic product names lead with the thing itself and qualify afterwards:
  // "جيب بنطلون كريب" is a SKIRT made of crepe, "عباية ستان حرير" is an ABAYA.
  // So the first word of the query decides the category and the rest only
  // refines it - without this, كريب/ستان pulled garment searches into the
  // fabric rows.
  function sharedPrefix(a, b) {
    var n = Math.min(a.length, b.length), i = 0;
    while (i < n && a.charAt(i) === b.charAt(i)) i++;
    return i;
  }

  // How strongly one label/handle answers the query.
  function strength(target, nq, qTokens, headOnly) {
    if (!target) return 0;
    var head = qTokens.length ? qTokens[0] : '';
    if (headOnly) {
      if (target === nq) return 100;
      if (!head) return 0;
      if (target === head) return 85;
      if (phraseHit(target, head)) return 78;
      // بلوزة vs بلوزات, عباية vs عبايات - Arabic plurals, matched on a
      // shared stem rather than a full stemmer.
      if (head.length >= 4 && target.length >= 4 && sharedPrefix(target, head) >= 4) return 72;
      if (phraseHit(nq, target)) return 60;
      if (phraseHit(target, nq)) return 55;
      return 0;
    }
    for (var k = 1; k < qTokens.length; k++) {
      if (target === qTokens[k]) return 45;
      if (phraseHit(target, qTokens[k])) return 38;
      if (qTokens[k].length >= 4 && target.length >= 4 && sharedPrefix(target, qTokens[k]) >= 4) return 32;
    }
    return 0;
  }

  function scoreRow(row, nq, qTokens) {
    var rowTotal = 0, i;
    for (i = 0; i < row.chips.length; i++) rowTotal += (row.chips[i].count || 0);

    var headBest = 0, tailBest = 0, pop = 0;
    function consider(target, count, bonus) {
      var h = strength(target, nq, qTokens, true);
      if (h) { h += bonus; if (h > headBest) { headBest = h; pop = count; } }
      var t = strength(target, nq, qTokens, false);
      if (t) { t += bonus; if (t > tailBest) { tailBest = t; if (!headBest) pop = count; } }
    }

    for (i = 0; i < row.aliases.length; i++) consider(norm(row.aliases[i]), rowTotal, 8);
    consider(norm(row.title), rowTotal, 8);
    consider(norm(row.handle), rowTotal, 8);
    for (i = 0; i < row.chips.length; i++) {
      consider(norm(row.chips[i].title), row.chips[i].count || 0, 0);
      consider(norm(row.chips[i].handle), row.chips[i].count || 0, 0);
    }

    if (!headBest && !tailBest) return 0;
    // The weaker signal only refines the stronger one, so "قماش صوف ليكرا"
    // beats "قماش كتان اسباني" for the winter row on its صوف evidence while
    // both share the same قماش head.
    var hi = Math.max(headBest, tailBest), lo = Math.min(headBest, tailBest);
    var score = hi + Math.round(0.35 * lo) + popBonus(pop);
    if (row.priority < 0) score -= 45;
    return score;
  }

  /* ------------------------------------------------------------ data shape */

  // Accepts v2 ({version,parents}) and the older bare {parentId:[chips]} map.
  function readRows(map) {
    var rows = [];
    var parents = (map && map.version === 2) ? map.parents : map;
    if (!parents) return rows;
    for (var pid in parents) {
      if (!Object.prototype.hasOwnProperty.call(parents, pid)) continue;
      var p = parents[pid];
      var isV2 = p && !Array.isArray(p);
      var chips = (isV2 ? p.chips : p) || [];
      chips = chips.filter(function (c) {
        return c && c.id && c.title && c.handle && c.count !== 0;
      });
      if (!chips.length) continue;
      rows.push({
        id: String(pid),
        title: isV2 ? (p.title || '') : '',
        aliases: (isV2 && p.aliases) ? p.aliases : [],
        priority: (isV2 && p.priority) ? p.priority : 0,
        kind: (isV2 && p.kind) ? p.kind : 'both',
        chips: chips
      });
    }
    return rows;
  }

  function rowForCollection(rows, currentId) {
    var i, j;
    for (i = 0; i < rows.length; i++) {
      if (rows[i].id === String(currentId)) return rows[i];
    }
    for (i = 0; i < rows.length; i++) {
      for (j = 0; j < rows[i].chips.length; j++) {
        if (String(rows[i].chips[j].id) === String(currentId)) return rows[i];
      }
    }
    return null;
  }

  function rowForSearch(rows, terms, wantKind) {
    var nq = norm(terms);
    if (!nq) return null;
    var qTokens = tokens(terms);

    // Answer a clothing query with clothing rows. Searching كارديجان كريب scored the كريب
    // FABRIC row highest on its كريب evidence, so a shopper looking for a cardigan got a row
    // of fabric chips. Rows tagged 'both' (promos, haberdashery) always stay eligible, and
    // with no hint nothing is filtered — so this can only ever narrow a wrong answer.
    if (wantKind === 'fabric' || wantKind === 'clothes') {
      var kept = [];
      for (var f = 0; f < rows.length; f++) {
        if (rows[f].kind === wantKind || rows[f].kind === 'both') kept.push(rows[f]);
      }
      if (kept.length) rows = kept;
    }

    // Two stages rather than one ranking, because a pure score gets this
    // wrong in ways that look arbitrary to a shopper. Searching ستان used to
    // open the 7-chip اتواب قماش row (its ستان corner holds 13 products) over
    // قماش صيفي, and popularity alone does not fix it either - the سواريه
    // row's ستان chip has 509 products, more than قماش صيفي's ستان حرير.
    //
    // So: if one of the curated rows (ملابس / قماش صيفي / قماش شتوي) answers
    // the query at all, it wins. Only when none of them match do we fall
    // through to the specific rows, ranked by relevance and popularity.
    var stages = [
      function (r) { return r.priority > 0; },
      function (r) { return true; }
    ];

    for (var s = 0; s < stages.length; s++) {
      var best = null, bestScore = 0;
      for (var i = 0; i < rows.length; i++) {
        if (!stages[s](rows[i])) continue;
        var sc = scoreRow(rows[i], nq, qTokens);
        // Tie-break on the richer row so the result never depends on object
        // key order (a bare "قماش" matches both fabric rows' aliases equally).
        if (sc > bestScore || (sc === bestScore && sc > 0 && best && rows[i].chips.length > best.chips.length)) {
          best = rows[i];
          bestScore = sc;
        }
      }
      if (best && bestScore >= MIN_SCORE) return best;
    }
    return null;
  }

  /* --------------------------------------------------------------- render */

  function hide(container) {
    container.innerHTML = '';
    var wrapper = container.closest ? container.closest('.related-collection-chips-wrapper') : null;
    (wrapper || container).style.display = 'none';
  }

  function thumbUrl(url) {
    if (!url) return null;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + THUMB;
  }

  function build(container, row, currentId) {
    // Preserved from the original implementation: the row reads reversed
    // relative to the JSON order.
    var chips = row.chips.slice().reverse();
    var frag = document.createDocumentFragment();

    chips.forEach(function (chip) {
      var isCurrent = currentId && String(chip.id) === String(currentId);
      var a = document.createElement('a');
      a.className = 'related-collection-chip' + (isCurrent ? ' is-current' : '');
      a.href = '/collections/' + encodeURIComponent(chip.handle);
      if (isCurrent) a.setAttribute('aria-current', 'page');

      var src = thumbUrl(chip.image);
      if (src) {
        var img = document.createElement('img');
        img.className = 'related-collection-chip__img';
        img.src = src;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.width = 34;
        img.height = 34;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.addEventListener('error', function () {
          a.classList.add('has-no-img');
          img.remove();
        });
        a.appendChild(img);
      } else {
        a.classList.add('has-no-img');
      }

      var label = document.createElement('span');
      label.className = 'related-collection-chip__label';
      label.textContent = chip.title;
      a.appendChild(label);
      frag.appendChild(a);
    });

    container.innerHTML = '';
    container.appendChild(frag);
    container.classList.add('is-ready');

    var active = container.querySelector('.is-current');
    if (active && typeof active.scrollIntoView === 'function') {
      try {
        active.scrollIntoView({ block: 'nearest', inline: 'center' });
      } catch (e) { /* older browsers: leave the row where it is */ }
    }
  }

  /* ----------------------------------------------------------------- init */

  function init() {
    var container = document.getElementById('related-collection-chips-container');
    if (!container) return;

    var currentId = container.dataset.currentCollectionId || '';
    var terms = container.dataset.searchTerms || '';
    // Fabric-vs-clothes hint set by mahitab-collection-chips.liquid. Searching كارديجان كريب
    // used to score the كريب FABRIC row highest on its كريب evidence and answer a clothing
    // query with a wall of fabric chips. Rows carry a `kind` of fabric/clothes/both in
    // CollectionChips.json; 'both' rows (promos, haberdashery) are never filtered out.
    var wantKind = container.dataset.kind || '';
    var jsonUrl = container.dataset.jsonUrl;
    if (!jsonUrl) return;

    fetch(jsonUrl)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (map) {
        var rows = readRows(map);
        var row = currentId ? rowForCollection(rows, currentId) : rowForSearch(rows, terms, wantKind);
        if (!row) return hide(container);
        build(container, row, currentId);
      })
      .catch(function () { hide(container); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
